import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserService } from '../Entities/AuthService';

/**
 * Hook customizado para gerenciar usuários com cache e prefetch
 * @param {object} filters - Filtros de busca
 * @param {number} page - Página atual
 * @param {number} limit - Itens por página
 * @returns {object} - Dados, loading, error e funções de prefetch
 */
export function useUsers(filters = {}, page = 1, limit = 10) {
  const queryClient = useQueryClient();

  // Criar chave única baseada nos filtros e página
  const queryKey = ['users', filters, page, limit];

  // Query principal (usa configuração global do main.jsx)
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const params = {
        page,
        limit,
        ...filters
      };
      
      const response = await UserService.list(params);
      
      // Adaptar resposta para formato esperado
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || page,
        totalPages: response.totalPages || Math.ceil((response.total || 0) / limit)
      };
    },
    // ✅ Configurações herdadas de main.jsx (global):
    // - staleTime: 30s (evita requisições excessivas)
    // - refetchOnMount: false (Socket.IO atualiza)
    // - refetchOnWindowFocus: false (Socket.IO atualiza)
    // - keepPreviousData: true (UX suave)
  });

  // Prefetch da próxima página
  const prefetchNextPage = () => {
    if (query.data && page < query.data.totalPages) {
      const nextPage = page + 1;
      const nextQueryKey = ['users', filters, nextPage, limit];
      
      queryClient.prefetchQuery({
        queryKey: nextQueryKey,
        queryFn: async () => {
          const params = { page: nextPage, limit, ...filters };
          const response = await UserService.list(params);
          return {
            data: response.data || [],
            total: response.total || 0,
            page: response.page || nextPage,
            totalPages: response.totalPages || Math.ceil((response.total || 0) / limit)
          };
        },
        // Usa configuração global
      });
    }
  };

  // Prefetch da página anterior
  const prefetchPreviousPage = () => {
    if (page > 1) {
      const prevPage = page - 1;
      const prevQueryKey = ['users', filters, prevPage, limit];
      
      queryClient.prefetchQuery({
        queryKey: prevQueryKey,
        queryFn: async () => {
          const params = { page: prevPage, limit, ...filters };
          const response = await UserService.list(params);
          return {
            data: response.data || [],
            total: response.total || 0,
            page: response.page || prevPage,
            totalPages: response.totalPages || Math.ceil((response.total || 0) / limit)
          };
        },
        // Usa configuração global
      });
    }
  };

  // Invalidar cache
  const invalidateCache = () => {
    queryClient.invalidateQueries(['users']);
  };

  return {
    data: query.data?.data || [],
    total: query.data?.total || 0,
    totalPages: query.data?.totalPages || 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    prefetchNextPage,
    prefetchPreviousPage,
    invalidateCache,
  };
}

export default useUsers;
